import React, {useEffect} from 'react';
import { createBrowserRouter, RouterProvider, Outlet,useMatches } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth, startLogin } from './contexts/AuthContext';
import { JobStatusProvider } from './contexts/JobStatusContext.jsx';
import { ToastProvider } from './components/ToastContainer';
import { ConfirmProvider } from './contexts/ConfirmDialogContext';
import Topbar from './components/Topbar';
import Home from './pages/Home';
import Processor from './pages/Processor';
import CarsData from './pages/CarsData';
import HistoryDetail from './pages/HistoryDetail';
import History from './pages/History';
import Analysis from './pages/Analysis';
import NotFound from './pages/NotFound';
import JobStatusDrawer from './components/JobStatusDrawer.jsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient();
// Composant qui gère la logique d'authentification
const ProtectedLayout = () => {
  const { isAuthenticating, isAuthenticated, currentUser, logout, authError, logoutError } = useAuth();
  const matches = useMatches();

  // Add this effect to update the title
  useEffect(() => {
    const matchWithTitle = matches.findLast((match) => match.handle?.title);
    if (matchWithTitle?.handle?.title) {
      document.title = `${matchWithTitle.handle.title} - Sellout Processor`; // You can add a suffix
    } else {
      document.title = 'Sellout Processor'; // Default title
    }
  }, [matches]);

  if (isAuthenticating) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-xl font-medium text-gray-700 dark:text-gray-200">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <Topbar currentUser={currentUser} logout={logout} />
      {logoutError && (
        <div className="mx-auto mt-4 max-w-xl rounded-md bg-red-100 p-3 text-red-700 dark:bg-red-900 dark:text-red-200">
          {logoutError}
        </div>
      )}
      {!isAuthenticated ? (
        <div className="mx-auto mt-10 max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
          <h1 className="mb-4 text-2xl font-bold text-gray-900 dark:text-white">Connexion</h1>
          <p className="mb-6 text-gray-700 dark:text-gray-200">
            {authError || "Vous êtes déconnecté."}
          </p>
          <button
            type="button"
            onClick={startLogin}
            className="inline-block rounded bg-blue-500 px-4 py-2 font-bold text-white hover:bg-blue-700"
          >
            Se connecter
          </button>
        </div>
      ) : (
        <>
          <Outlet />
          <JobStatusDrawer />
        </>
      )}
    </div>
  );
};

// Configuration des routes
const router = createBrowserRouter([
  {
    path: "/",
    element: <ProtectedLayout />,
    children: [
      {
        path: "/",
        element: <Home />,
        handle: { title: "Accueil" } // Custom title
      },
      {
        path: "/processor",
        element: <Processor />,
        handle: { title: "Processeur" }
      },
      {
        path: "/processor/history",
        element: <History />,
        handle: { title: "Historique" }
      },
      {
        path: "/processor/analysis",
        element: <Analysis />,
        handle: { title: "Analyse" }
      },
      {
        path: "/processor/history/:uuid",
        element: <HistoryDetail />,
        handle: { title: "Détail Historique" }
      },
      {
        path: "/cars-data",
        element: <CarsData />,
        handle: { title: "Données Véhicules" }
      },
      {
        // Any other path: inside the layout, so the top bar and menu stay available
        path: "*",
        element: <NotFound />,
        handle: { title: "Page introuvable" }
      }
    ]
  }
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <ToastProvider>
            <ConfirmProvider>
              <JobStatusProvider>
                <RouterProvider router={router} />
              </JobStatusProvider>
            </ConfirmProvider>
          </ToastProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
export default App;
